import Icon from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Space, Tag as ATag, Tooltip } from 'antd';
import { ReactComponent as ForwardSvg } from 'assets/icons/forwardCurrentColor.svg';
import ContactWhatsapp from 'components/ContactWhatsapp';
import SellerCenter from 'components/SellerCenter';
import { formatNumber } from 'utils/numberFormat';
import { useTranslate } from '@tolgee/react';
import { ReturnOrderListItemV2 } from 'services/api/refund';
import { buyerNickMask } from 'services/channels/demo';
import { toUpperCase } from 'utils/toLowerCase';
import { usePlatform } from '../../hooks/usePlatformList';
import Product from '../Product';
import { OrderStatusCell } from '../OrderStatusCell';
import { OrderRemark } from '../OrderRemark';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { ReturnActions } from '../ReturnActions';
import { ConversationTag } from 'views/Conversation/ConvList/ConversationTag';
import { DisplayNumber } from './DisplayNumber';
import { i18nExist } from 'services/i18n/utils';
import { BuyerInfo } from './BuyerInfo';
import { ReactComponent as CSIcon } from '../../assets/cs.svg';
import { Logistics, ReverseLogistics } from './Logistics';
import {
  useReturnItemPermissions,
  useReturnItemDisplayState,
  useReturnItemLabels,
  useShopeeSellerRefundContent,
  useGoSellerCenter,
} from './hooks';

interface Props {
  row: ReturnOrderListItemV2 & {
    logisticsList?: Conversation.Trade.Logistics[];
    reverseLogisticsList?: Conversation.Trade.ReverseLogistics[];
  };
  refreshList: () => void;
  onClick: () => void;
  conversation?: Conversation.Conversation;
}

const TrReturn = styled.ul`
  min-height: 96px;
  padding: 0;
  /* width: calc(100vw - 128px); */
  align-items: flex-start;
  &:hover {
    background-color: var(--fett-bg-subtle);
    cursor: pointer;
  }
  li {
    padding-top: 17px !important;
  }
  .td-reason,
  .td-rqReason {
    > div {
      width: 100%;
      overflow: hidden;
      .ant-tag {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
  .td-action {
    button {
      display: block;
      max-width: 100%;
      height: auto;
      line-height: 20px;
      margin: 0 0 6px;
      border: none;
      background: none;
      color: var(--fett-text-secondary);
      padding: 0;
      white-space: pre-wrap;
      box-shadow: none;
      text-align: left;

      &.agree {
        color: var(--fett-surface-error-text);
      }
      &.contact-lazada {
        color: var(--fett-scenario-accent-text);
      }
      &:hover {
        color: var(--fett-scenario-accent-text);
      }
    }
    .ant-btn:focus,
    .ant-btn:hover {
      background: initial;
    }
    .ant-btn[disabled],
    .ant-btn[disabled]:active,
    .ant-btn[disabled]:focus,
    .ant-btn[disabled]:hover {
      color: rgba(0, 0, 0, 0.25);
      border: none;
      background: initial;
    }
    .moreBtns {
      background-color: var(--fett-bg-panel);
      padding: 8px;
      border: 1px solid var(--fett-border-default);
      border-radius: 4px;
      min-width: 180px;
      .ant-btn {
        width: 100%;
        height: 28px;
        line-height: 28px;
        box-shadow: none;
        text-align: left;
      }
    }
    .anticon-down {
      font-size: 12px;
      color: var(--fett-text-tertiary);
    }
  }
`;

const TrOrderInfo = styled.ul`
  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #f2f8ff;
    [data-color-mode='dark'] & {
      background-color: var(--fett-bg-subtle);
    }
    height: 42px;
    .anticon-copy {
      margin-left: 4px;
    }
    .storeName {
      display: inline-block;
      max-width: 126px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .storeType {
      text-align: center;
      padding: 0 8px;
      max-width: 100px;
      height: 23px;
      line-height: 23px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      background: var(--fett-bg-panel);
      border: 1px solid var(--fett-border-strong);
      color: var(--fett-text-tertiary);
      font-size: 12px;
    }
    .returnType {
      text-align: center;
      padding: 0 8px;
      height: 23px;
      line-height: 23px;
      background: var(--fett-bg-panel);
      border: 1px solid rgba(251, 206, 206, 1);
      color: var(--fett-surface-error-text);
      font-size: 12px;
    }
    .buyerNick {
      width: 140px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

const SpecialStatusTimetip = styled.div`
  height: 22px;
  line-height: 22px;
  border-radius: 3px;
  background: #f05b5b;
  width: fit-content;
  padding: 0 8px;
  color: white;
  font-size: 12px;
  margin-bottom: 2px;
`;

const PreOrderTag = styled(ATag)`
  border: none;
  color: var(--fett-scenario-success-text);
  padding: 0 4px;
  font-size: 12px;
  background: var(--fett-surface-success-bg);
`;

/** 去卖家中心入口（纯 UI，跳转逻辑在 useGoSellerCenter） */
export function GoSellerCenter(props: {
  storeId: string;
  orderId: string;
  platform: General.PlatformUpperCase;
}) {
  const { storeId, orderId, platform } = props;
  const { t } = useTranslate();
  const { handleGoSellerCenter } = useGoSellerCenter();

  return (
    <SellerCenter platform={platform}>
      <Button onClick={() => handleGoSellerCenter({ storeId, orderId, platform })}>
        {t('scene.orderWorkspace.returnRefundTab.action.viewInSellerCenter')}
        <Icon component={ForwardSvg} />
      </Button>
    </SellerCenter>
  );
}

const ReturnListItem = (props: Props) => {
  const { row, refreshList, onClick, conversation } = props;
  const platform = usePlatform()!;
  const { t } = useTranslate();

  const {
    region,
    storeName,
    storeAlias,
    returnItemData: items,
    reason,
    reassessedRequestReason,
    reasonName,
    unit,
    refundAmount,
    externalStoreId,
    storeType,
    sellerType,
    returnType,
    containsPreOrderItem,
    phone,
    leChatStatus,
    internalTradeId,
    lechatSellerMemos,
    sellerMemos,
    reputation,
    hasIncentive,
    internalStoreId,
    logisticsList,
    reverseLogisticsList,
    reverseTag,
    returnStatus,
  } = row;

  const { operatable, contactEnable, showStoreType, showSellerType } = useReturnItemPermissions({
    platform,
    region,
    internalStoreId,
    returnStatus,
  });

  const {
    storeDisplayName,
    getRegionLabel,
    getReasonLabel,
    getMercadoReasonLabel,
    returnTypeDisplay,
  } = useReturnItemLabels({
    platform,
    region,
    storeName,
    storeAlias,
  });

  const {
    returnStatusTip,
    shopeeTimeTip,
    tkTimeTip,
    formattedShopeeTime,
    formattedNonMercadoTime,
    formattedTikTokOrKwaiTime,
    formattedMercadoTime,
  } = useReturnItemDisplayState({ row, platform, operatable });

  const shopeeSellerRefundContent = useShopeeSellerRefundContent({
    platform,
    storeType,
    externalStoreId,
    orderSn: row.orderSn,
    returnSn: row.returnSn,
  });

  return (
    <>
      <TrOrderInfo>
        <li>
          <div className="inline-flex items-center gap-4">
            {showStoreType && (
              <>
                <div className="returnType">{returnTypeDisplay(returnType)}</div>
                <div className="storeType">
                  <span>{t(`店铺类型枚举.${storeType}`)}</span>
                  {showSellerType && i18nExist(`卖家级别枚举.${sellerType}`) && (
                    <span className="ml-1">{t(`卖家级别枚举.${sellerType}`)}</span>
                  )}
                </div>
              </>
            )}
            <span>{getRegionLabel(region)}</span>
            <Tooltip title={storeDisplayName}>
              <span className="storeName">{storeDisplayName}</span>
            </Tooltip>
            {containsPreOrderItem ? (
              <PreOrderTag color="green" title={t('domain.product.type.preOrder')}>
                {t('domain.product.type.preOrder')}
              </PreOrderTag>
            ) : null}
            <DisplayNumber data={row} />
            {reputation && (
              <ConversationTag
                className="bg-[#F56E6E]"
                text={t('scene.orderWorkspace.returnRefundTab.list.column.reputationImpact')}
              />
            )}
            {reputation && hasIncentive && (
              <div style={{ fontSize: 12, color: 'var(--fett-surface-warning-text)' }}>
                {t('解决该问题将不会影响您的店铺信誉。')}
              </div>
            )}
          </div>
          <Space size="small">
            <BuyerInfo
              row={row}
              conversation={conversation}
              contactEnable={contactEnable}
              type="refund"
              onClose={refreshList}
            />
            {conversation?.receptionName ? (
              <Tooltip
                title={`${t('scene.orderWorkspace.returnRefundTab.list.column.assignedAgent')}: ${
                  conversation.receptionName
                }`}
              >
                <div className="flex rounded-sm cursor-pointer items-center h-[24px] px-[4px] border border-solid border-[var(--fett-border-strong)] bg-[var(--fett-bg-panel)]">
                  <Icon component={CSIcon} />
                </div>
              </Tooltip>
            ) : null}
            {conversation && operatable && (
              <ContactWhatsapp
                orderPhone={phone}
                region={conversation.region}
                channel={conversation.channel}
                externalStoreId={externalStoreId}
                externalBuyerId={phone}
                buyerNick={buyerNickMask(conversation.buyerNick)}
                storeName={conversation.storeName}
                orderId={row.orderSn}
                talkPhone={conversation.phone}
              />
            )}
            {shopeeSellerRefundContent}
          </Space>
        </li>
      </TrOrderInfo>
      <TrReturn key={items[0].itemId} onClick={onClick}>
        <li className="td-title">
          <Product
            info={items}
            unit={unit}
            externalStoreId={externalStoreId}
            platform={platform}
            region={toUpperCase(region)}
          />
        </li>
        <li className="td-amount">
          {refundAmount ? unit : ''}
          {formatNumber(refundAmount, '0,0.00')}
        </li>
        <li className="td-reason">
          <div className="flex flex-col gap-1">
            <div>
              <ATag>
                {platform === 'MERCADO'
                  ? getMercadoReasonLabel(reasonName)
                  : getReasonLabel(reason, platform)}
              </ATag>
            </div>
            <OrderRemark
              content={platform === 'SHOPEE' ? sellerMemos ?? '' : lechatSellerMemos ?? ''}
              internalTradeId={internalTradeId}
              platform={platform}
              onRefresh={refreshList}
              showEdit
            />
          </div>
        </li>
        {platform === 'SHOPEE' && (
          <li className="td-rqReason">
            <div>
              <div>
                {reassessedRequestReason && reassessedRequestReason !== 'NONE' ? (
                  <ATag>{getReasonLabel(reassessedRequestReason, platform)}</ATag>
                ) : (
                  '-'
                )}
              </div>
            </div>
          </li>
        )}
        {isTiKTokUpperCase(platform) && (
          <li className="td-amount">
            <OrderStatusCell
              leChatStatus={leChatStatus}
              internalTradeId={internalTradeId}
              reverseTag={reverseTag}
            />
          </li>
        )}
        <li className="td-status">
          {shopeeTimeTip && (
            <SpecialStatusTimetip>{shopeeTimeTip.replyDisplay}</SpecialStatusTimetip>
          )}
          {tkTimeTip && <SpecialStatusTimetip>{tkTimeTip.text}</SpecialStatusTimetip>}
          <div>{returnStatusTip}</div>
          <div>
            {formattedShopeeTime || formattedTikTokOrKwaiTime || formattedNonMercadoTime}
            {formattedMercadoTime}
          </div>
        </li>
        {platform === 'SHOPEE' && [
          <li>
            {logisticsList?.length ? (
              <Logistics
                platform={platform}
                region={toUpperCase(region)}
                externalTradeId={row.orderSn}
                logisticsList={logisticsList}
              />
            ) : (
              '-'
            )}
          </li>,
          <li>
            {reverseLogisticsList?.filter((_) => _.externalReturnId === row.returnSn).length ? (
              <ReverseLogistics
                platform={platform}
                region={toUpperCase(region)}
                externalReturnId={row.returnSn}
                externalTradeId={row.orderSn}
                reverseLogisticsList={reverseLogisticsList}
              />
            ) : (
              '-'
            )}
          </li>,
        ]}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <li className="td-action" onClick={(e) => e.stopPropagation()}>
          {operatable && (
            <ReturnActions
              conversation={conversation}
              platform={platform}
              region={toUpperCase(region)}
              row={row}
              refreshList={refreshList}
            />
          )}
          <Button type="link" size="small" onClick={onClick}>
            {t('scene.orderWorkspace.returnRefundTab.list.action.viewDetail')}
          </Button>
        </li>
      </TrReturn>
    </>
  );
};
export default ReturnListItem;
